import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Peer from 'simple-peer'
import { jwtDecode } from 'jwt-decode'
import toast from 'react-hot-toast'
import { mediaFileUrl } from './baseUrl'
import axiosWrapper from './utils/AxiosWrapper'
import { showBrowserNotification } from './utils/browserNotify'
import { getPortalSocket } from './utils/portalSocket'
import { chatFocusState, resetChatFocusState } from './utils/chatFocusState'

const detectSingleLink = (text) => {
  const t = String(text || '').trim()
  if (!t) return null
  if (/^https?:\/\/\S+$/i.test(t)) return t
  const m = t.match(/https?:\/\/[^\s]+/i)
  return m ? m[0] : null
}

const guessImageFromMimeOrName = (mime, name) => {
  if (mime && /^image\//i.test(mime)) return true
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(String(name || ''))
}

const GroupMessageBody = ({ chat }) => {
  const mt = chat.messageType || 'text'
  if (mt === 'image' && chat.attachmentUrl) {
    return (
      <span style={{ display: 'block' }}>
        {chat.text ? (
          <div style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>{chat.text}</div>
        ) : null}
        <img
          src={mediaFileUrl(chat.attachmentUrl)}
          alt=""
          style={{
            maxWidth: '100%',
            maxHeight: 280,
            borderRadius: 8,
            display: 'block',
          }}
        />
      </span>
    )
  }
  if (mt === 'file' && chat.attachmentUrl) {
    return (
      <span style={{ display: 'block' }}>
        {chat.text ? (
          <div style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>{chat.text}</div>
        ) : null}
        <a
          href={mediaFileUrl(chat.attachmentUrl)}
          download
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0369a1', fontWeight: 600, wordBreak: 'break-all' }}
        >
          📎 {chat.fileName || 'Download file'}
        </a>
      </span>
    )
  }
  if (mt === 'link') {
    const href = chat.linkUrl || detectSingleLink(chat.text)
    if (!href) return chat.text || ''
    return (
      <span style={{ display: 'block' }}>
        {chat.text && chat.text.trim() !== href ? (
          <div style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>{chat.text}</div>
        ) : null}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0369a1', wordBreak: 'break-all' }}
        >
          {href}
        </a>
      </span>
    )
  }
  return chat.text || ''
}

const genChatMsgId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

const normPhone = (p) => String(p ?? '').trim()

const formatSidebarTime = (v) => {
  if (!v) return ''
  const d = new Date(v)
  const t = d.getTime()
  if (Number.isNaN(t)) return ''
  const diff = Date.now() - t
  if (diff < 60_000) return 'Now'
  if (diff < 86_400_000) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
  if (diff < 7 * 86_400_000) {
    return d.toLocaleDateString(undefined, { weekday: 'short' })
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const formatLastSeenLabel = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const t = d.getTime()
  if (Number.isNaN(t)) return ''
  const diff = Date.now() - t
  if (diff < 90_000) return 'last seen just now'
  if (diff < 86_400_000) {
    return `last seen today at ${d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`
  }
  return `last seen ${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}

const MessageReceiptTick = ({ receipt }) => {
  const r = receipt || 'sent'
  const gray = '#667781'
  const readBlue = '#34b7f1'
  if (r === 'sending') {
    return (
      <span style={{ color: gray, fontSize: '11px', opacity: 0.75 }}>…</span>
    )
  }
  if (r === 'sent') {
    return <span style={{ color: gray, fontSize: '13px', lineHeight: 1 }}>✓</span>
  }
  if (r === 'delivered') {
    return (
      <span style={{ color: gray, fontSize: '13px', lineHeight: 1, letterSpacing: '-0.12em' }}>
        ✓✓
      </span>
    )
  }
  return (
    <span style={{ color: readBlue, fontSize: '13px', lineHeight: 1, letterSpacing: '-0.12em' }}>
      ✓✓
    </span>
  )
}

const socket = getPortalSocket()

const CommunicationHub = () => {
  const navigate = useNavigate()

  const navigateToPortalChat = useCallback(() => {
    const portal = String(localStorage.getItem('userType') || '').toLowerCase()
    if (portal === 'faculty') {
      navigate('/faculty/chat')
      return
    }
    if (portal === 'admin') {
      navigate('/admin?page=chat')
      return
    }
    navigate('/student?page=chat')
  }, [navigate])

  const token = localStorage.getItem('userToken')
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  )
  const userData = token ? jwtDecode(token) : {}
  const isAdmin = userData.role === 'admin'

  // Extracting senderName from userData (from JWT)
  const myDisplayName = userData.senderName || 'User'

  const [view, setView] = useState('direct')
  const [contacts, setContacts] = useState([])
  const [groups, setGroups] = useState([])
  const [activeChat, setActiveChat] = useState(null)
  const [chatLog, setChatLog] = useState([])
  const [message, setMessage] = useState('')
  const [recipientInput, setRecipientInput] = useState('')

  const [showGroupModal, setShowGroupModal] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [selectedMembers, setSelectedMembers] = useState([])

  const [showGroupInfo, setShowGroupInfo] = useState(false)
  const [groupInfoLoading, setGroupInfoLoading] = useState(false)
  const [groupInfo, setGroupInfo] = useState(null)
  const groupFileRef = useRef(null)

  const [callStatus, setCallStatus] = useState('idle')
  const [receivingCall, setReceivingCall] = useState(false)
  const [callerInfo, setCallerInfo] = useState({
    phone: '',
    name: '',
    signal: null,
  })

  // Calling/session state (used for both-side hangup + device switching)
  const [callTargetPhone, setCallTargetPhone] = useState('')
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] })
  const [micDeviceId, setMicDeviceId] = useState('')
  const [speakerDeviceId, setSpeakerDeviceId] = useState('')
  const [deviceError, setDeviceError] = useState('')

  const [peerPresence, setPeerPresence] = useState({
    online: false,
    lastSeenAt: null,
  })
  const [peerTyping, setPeerTyping] = useState(false)

  const connectionRef = useRef()
  const localStreamRef = useRef(null)
  const remoteAudio = useRef()
  const chatEndRef = useRef(null)
  const activeChatRef = useRef(null)
  const viewRef = useRef('direct')
  const typingStopTimerRef = useRef(null)
  const peerTypingHideRef = useRef(null)

  useEffect(() => {
    activeChatRef.current = activeChat
  }, [activeChat])
  useEffect(() => {
    viewRef.current = view
  }, [view])

  useEffect(() => {
    if (view === 'direct') {
      chatFocusState.view = 'direct'
      chatFocusState.directPeer = activeChat
        ? normPhone(activeChat.contactPhone)
        : ''
      chatFocusState.groupId = ''
    } else {
      chatFocusState.view = 'groups'
      chatFocusState.directPeer = ''
      chatFocusState.groupId = activeChat ? String(activeChat._id || '') : ''
    }
    return () => resetChatFocusState()
  }, [view, activeChat])

  const fetchInitialData = useCallback(async () => {
    try {
      const portal = (localStorage.getItem('userType') || '').toLowerCase()
      const phoneEnc = encodeURIComponent(normPhone(userData.phone))
      const [contactRes, groupRes] = await Promise.all([
        axiosWrapper.get(
          `/chat/my-contacts/${phoneEnc}?portal=${encodeURIComponent(portal)}`,
          {
            headers: authHeader,
          }
        ),
        axiosWrapper.get(
          `/groups/user/${encodeURIComponent(
            normPhone(userData.phone)
          )}?role=${encodeURIComponent(String(userData.role || ''))}`,
          { headers: authHeader }
        ),
      ])
      if (contactRes.data.success) setContacts(contactRes.data.contacts || [])
      if (groupRes.data.success) {
        setGroups(groupRes.data.groups || [])
        groupRes.data.groups.forEach((g) =>
          socket.emit('join_group', g._id)
        )
      }
    } catch (err) {
      console.error('Data load error', err)
    }
  }, [userData.phone, userData.role, authHeader])

  const fetchGroupsOnly = useCallback(async () => {
    const res = await axiosWrapper.get(
      `/groups/user/${encodeURIComponent(
        normPhone(userData.phone)
      )}?role=${encodeURIComponent(String(userData.role || ''))}`,
      { headers: authHeader }
    )
    if (res.data.success) setGroups(res.data.groups || [])
  }, [userData.phone, userData.role, authHeader])

  const refreshPeerPresence = useCallback(
    async (contactPhone) => {
      try {
        const ph = encodeURIComponent(normPhone(contactPhone))
        const res = await axiosWrapper.get(`/chat/presence/${ph}`, {
          headers: authHeader,
        })
        if (res.data?.success) {
          setPeerPresence({
            online: !!res.data.online,
            lastSeenAt: res.data.lastSeenAt ?? null,
          })
        }
      } catch {
        /* ignore */
      }
    },
    [authHeader]
  )

  useEffect(() => {
    if (view !== 'direct' || !activeChat?.contactPhone) return undefined
    refreshPeerPresence(activeChat.contactPhone)
    const id = window.setInterval(
      () => refreshPeerPresence(activeChat.contactPhone),
      8000
    )
    const onVis = () => {
      if (!document.hidden) refreshPeerPresence(activeChat.contactPhone)
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [view, activeChat?.contactPhone, refreshPeerPresence])

  const endCall = useCallback(
    (shouldNotifyRemote = true) => {
      // Stop local mic stream (if any).
      if (localStreamRef.current?.getTracks) {
        localStreamRef.current.getTracks().forEach((t) => t.stop())
      }
      localStreamRef.current = null

      // Destroy WebRTC peer connection (if any).
      try {
        connectionRef.current?.destroy?.()
      } catch (e) {
        // noop
      }
      connectionRef.current = null

      if (remoteAudio.current) {
        remoteAudio.current.srcObject = null
      }

      setCallStatus('idle')
      setReceivingCall(false)
      setCallerInfo({ phone: '', name: '', signal: null })

      setCallTargetPhone('')
      setDeviceError('')

      if (shouldNotifyRemote && callTargetPhone) {
        socket.emit('end_call', { to: callTargetPhone, from: userData.phone })
      }
    },
    [callTargetPhone, userData.phone]
  )

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatLog])

  useEffect(() => {
    if (!chatLog.some((m) => m.highlight)) return undefined
    const t = setTimeout(() => {
      setChatLog((prev) =>
        prev.map((m) => (m.highlight ? { ...m, highlight: false } : m))
      )
    }, 2800)
    return () => clearTimeout(t)
  }, [chatLog])

  useEffect(() => {
    const self = normPhone(userData.phone)
    if (!self) return undefined

    const onSocketConnect = () => {
      socket.emit('join_app', self)
    }
    socket.on('connect', onSocketConnect)
    socket.emit('join_app', self)
    fetchInitialData()

    const emitReadReceipt = (peerPhone) => {
      const p = normPhone(peerPhone)
      if (!p) return
      socket.emit('read_receipt', { peerPhone: p })
    }

    const onReceiveDm = (data) => {
      const name = data.senderName || data.sender
      const raw = String(data.message || '').trim()
      const preview = raw.length > 90 ? `${raw.slice(0, 90)}…` : raw
      const listPreview = raw.length > 72 ? `${raw.slice(0, 72)}…` : raw
      if (data.messageId) {
        socket.emit('delivery_ack', {
          messageId: data.messageId,
          readerPhone: normPhone(userData.phone),
        })
      }

      if (data.sender && normPhone(data.sender) !== normPhone(userData.phone)) {
        const snd = normPhone(data.sender)
        const nowIso = new Date().toISOString()
        const viewingThisDirect =
          viewRef.current === 'direct' &&
          normPhone(activeChatRef.current?.contactPhone) === snd
        setContacts((prev) => {
          const idx = prev.findIndex((c) => normPhone(c.contactPhone) === snd)
          if (idx < 0) return prev
          const cur = prev[idx]
          const updated = {
            ...cur,
            lastMessagePreview: listPreview,
            lastMessageAt: nowIso,
            highlightFromOther: !viewingThisDirect,
            lastMessageFromSelf: false,
          }
          const rest = prev.filter((_, i) => i !== idx)
          return [updated, ...rest]
        })

        if (!viewingThisDirect) {
          showBrowserNotification(`${name || 'Contact'} messaged you`, preview || '', {
            tag: `dm-${snd}`,
            navigateFn: navigateToPortalChat,
          })
        }

        setPeerPresence((prev) => ({ ...prev, online: true, lastSeenAt: nowIso }))

        if (viewingThisDirect) {
          setTimeout(() => emitReadReceipt(data.sender), 220)
        }
      }

      if (
        viewRef.current === 'direct' &&
        normPhone(activeChatRef.current?.contactPhone) === normPhone(data.sender)
      ) {
        const mid = data.messageId ? String(data.messageId) : genChatMsgId()
        setChatLog((prev) => [
          ...prev,
          {
            id: mid,
            dbId: data.messageId ? String(data.messageId) : null,
            sender: data.sender,
            senderLabel: data.senderName || data.sender,
            text: data.message,
            highlight: true,
          },
        ])
      }
    }

    const onRecvGroup = (data) => {
      const inThisGroup =
        viewRef.current === 'groups' &&
        String(activeChatRef.current?._id) === String(data.groupId)
      const fromSelf = normPhone(data.senderPhone) === normPhone(userData.phone)
      if (!inThisGroup && !fromSelf) {
        const who = data.senderName?.trim() || 'Member'
        const raw =
          data.messageType === 'file' || data.messageType === 'image'
            ? data.fileName || 'Attachment'
            : String(data.message || '').trim()
        const prv = raw.length > 72 ? `${raw.slice(0, 72)}…` : raw
        showBrowserNotification(`${who} (group)`, prv || 'New group message', {
          tag: `grp-${String(data.groupId || '')}`,
          navigateFn: navigateToPortalChat,
        })
      }
      if (
        viewRef.current === 'groups' &&
        String(activeChatRef.current?._id) === String(data.groupId)
      ) {
        setChatLog((prev) => [
          ...prev,
          {
            id: genChatMsgId(),
            sender: fromSelf ? 'Me' : data.senderName || 'Member',
            senderLabel: fromSelf ? 'You' : data.senderName || 'Member',
            text: data.message || '',
            messageType: data.messageType || 'text',
            attachmentUrl: data.attachmentUrl || '',
            linkUrl: data.linkUrl || '',
            fileName: data.fileName || '',
            highlight: !fromSelf,
          },
        ])
      }
    }

    const onGroupMessagesCleared = (payload) => {
      const gid = String(payload?.groupId || '')
      if (
        viewRef.current === 'groups' &&
        String(activeChatRef.current?._id) === gid
      ) {
        setChatLog([])
        toast.success('Group chat was cleared')
      }
    }

    const onGroupRemoved = (payload) => {
      const gid = String(payload?.groupId || '')
      setGroups((prev) => prev.filter((g) => String(g._id) !== gid))
      if (String(activeChatRef.current?._id) === gid) {
        setActiveChat(null)
        setChatLog([])
        setShowGroupInfo(false)
        setGroupInfo(null)
        toast.success('This group was deleted')
      }
    }

    const onMessageSaved = (data) => {
      const cid = data?.clientMsgId
      const mid = data?.messageId ? String(data.messageId) : null
      if (!mid || !cid) return
      setChatLog((prev) =>
        prev.map((m) =>
          m.clientMsgId === cid && m.sender === 'Me'
            ? { ...m, dbId: mid, receipt: 'sent' }
            : m
        )
      )
    }

    const onMessageStatus = (data) => {
      if (!data?.messageId || !data.delivered) return
      const mid = String(data.messageId)
      setChatLog((prev) =>
        prev.map((m) =>
          m.dbId === mid && m.sender === 'Me'
            ? {
                ...m,
                receipt: m.receipt === 'read' ? 'read' : 'delivered',
              }
            : m
        )
      )
    }

    const onMessagesRead = (payload) => {
      const reader = normPhone(payload?.readerPhone)
      if (!reader) return
      const peerNow = normPhone(activeChatRef.current?.contactPhone)
      if (peerNow !== reader) return
      setChatLog((prev) =>
        prev.map((m) =>
          m.sender === 'Me' ? { ...m, receipt: 'read' } : m
        )
      )
    }

    const onPeerTyping = ({ fromPhone }) => {
      const peer = normPhone(activeChatRef.current?.contactPhone)
      if (normPhone(fromPhone) !== peer) return
      setPeerTyping(true)
      if (peerTypingHideRef.current)
        window.clearTimeout(peerTypingHideRef.current)
      peerTypingHideRef.current = window.setTimeout(
        () => setPeerTyping(false),
        4500
      )
    }

    const onPeerStoppedTyping = ({ fromPhone }) => {
      const peer = normPhone(activeChatRef.current?.contactPhone)
      if (normPhone(fromPhone) !== peer) return
      setPeerTyping(false)
    }

    const onAddedGroup = (data) => {
      socket.emit('join_group', data.groupId)
      fetchGroupsOnly()
      alert(`Admin added you to group: ${data.groupName}`)
    }

    socket.on('receive_message', onReceiveDm)
    socket.on('receive_group_message', onRecvGroup)
    socket.on('group_messages_cleared', onGroupMessagesCleared)
    socket.on('group_removed', onGroupRemoved)
    socket.on('message_saved', onMessageSaved)
    socket.on('message_status', onMessageStatus)
    socket.on('messages_read', onMessagesRead)
    socket.on('peer_typing', onPeerTyping)
    socket.on('peer_stopped_typing', onPeerStoppedTyping)
    socket.on('added_to_new_group', onAddedGroup)

    socket.on('incoming_call', (data) => {
      setReceivingCall(true)
      setCallerInfo({
        phone: data.from,
        name: data.name,
        signal: data.signal,
      })
      const label = String(data?.name || '').trim() || String(data?.from || 'Someone')
      showBrowserNotification('Incoming voice call', `${label}`, {
        tag: 'incoming-voice-call',
        force: true,
      })
    })

    socket.on('user_is_ringing', () => {
      setCallStatus('ringing')
    })

    socket.on('user_unavailable', () => {
      endCall()
      setCallStatus('idle')
    })

    socket.on('call_ended', () => {
      endCall(false)
      setCallStatus('idle')
    })

    socket.on('call_accepted', (signal) => {
      setCallStatus('connected')
      if (connectionRef.current) connectionRef.current.signal(signal)
    })

    return () => {
      socket.off('connect', onSocketConnect)
      socket.off('receive_message', onReceiveDm)
      socket.off('receive_group_message', onRecvGroup)
      socket.off('group_messages_cleared', onGroupMessagesCleared)
      socket.off('group_removed', onGroupRemoved)
      socket.off('message_saved', onMessageSaved)
      socket.off('message_status', onMessageStatus)
      socket.off('messages_read', onMessagesRead)
      socket.off('peer_typing', onPeerTyping)
      socket.off('peer_stopped_typing', onPeerStoppedTyping)
      socket.off('added_to_new_group', onAddedGroup)
      socket.off('incoming_call')
      socket.off('user_is_ringing')
      socket.off('user_unavailable')
      socket.off('call_ended')
      socket.off('call_accepted')
    }
  }, [
    userData.phone,
    fetchInitialData,
    fetchGroupsOnly,
    endCall,
    navigateToPortalChat,
  ])

  const refreshAudioDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return
      const devices = await navigator.mediaDevices.enumerateDevices()
      const inputs = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || 'Microphone',
        }))
      const outputs = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || 'Speaker',
        }))

      setAudioDevices({ inputs, outputs })
      setMicDeviceId((prev) => {
        if (prev && inputs.some((i) => i.deviceId === prev)) return prev
        return inputs[0]?.deviceId || ''
      })
      setSpeakerDeviceId((prev) => {
        if (prev && outputs.some((o) => o.deviceId === prev)) return prev
        return outputs[0]?.deviceId || ''
      })
    } catch (e) {
      // Enumeration can fail without permissions; UI will fallback.
    }
  }, [])

  const switchMicrophone = useCallback(async () => {
    try {
      const peer = connectionRef.current
      const currentStream = localStreamRef.current
      const currentTrack = currentStream?.getAudioTracks?.()[0]
      if (!peer || !currentTrack) return
      if (!micDeviceId) return

      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: micDeviceId } },
      })
      const newTrack = newStream.getAudioTracks()[0]
      if (!newTrack) {
        newStream.getTracks().forEach((t) => t.stop())
        return
      }

      if (typeof peer.replaceTrack === 'function') {
        peer.replaceTrack(currentTrack, newTrack, newStream)
        // Stop old track to avoid echo/extra CPU.
        currentTrack.stop()
        localStreamRef.current = newStream
        setDeviceError('')
      } else {
        newStream.getTracks().forEach((t) => t.stop())
        setDeviceError('Mic switching not supported during active calls.')
      }
    } catch (e) {
      setDeviceError('Could not switch microphone. Please try again.')
    }
  }, [micDeviceId])

  const switchSpeaker = useCallback(async () => {
    try {
      const el = remoteAudio.current
      if (!el) return
      if (typeof el.setSinkId !== 'function') {
        setDeviceError('Speaker switching not supported in this browser.')
        return
      }
      if (!speakerDeviceId) return
      await el.setSinkId(speakerDeviceId)
      setDeviceError('')
    } catch (e) {
      setDeviceError('Could not switch speaker. Please try again.')
    }
  }, [speakerDeviceId])

  const verifyAndAddContact = async () => {
    if (!recipientInput) return
    try {
      const res = await axiosWrapper.get(
        `/contacts/search/${recipientInput}`,
        { headers: authHeader }
      )
      if (res.data.success) {
        const found = res.data.data
        const fullName = `${found.firstName} ${found.lastName}`
        await axiosWrapper.post(
          `/chat/save-contact`,
          {
            ownerPhone: normPhone(userData.phone),
            contactPhone: found.phone,
            contactName: fullName,
          },
          { headers: authHeader }
        )
        setContacts((prev) => [
          { contactPhone: found.phone, contactName: fullName },
          ...prev,
        ])
        setRecipientInput('')
      }
    } catch (err) {
      alert('User not found')
    }
  }

  const selectContact = async (contact) => {
    setPeerTyping(false)
    setActiveChat(contact)
    try {
      const me = normPhone(userData.phone)
      const theirs = encodeURIComponent(normPhone(contact.contactPhone))
      const mine = encodeURIComponent(me)
      const res = await axiosWrapper.get(`/chat/history/${mine}/${theirs}`, {
        headers: authHeader,
      })
      setChatLog(
        (res.data.messages || []).map((m, i) => {
          const fromMe = normPhone(m.senderPhone) === me
          let receipt
          if (fromMe) {
            if (m.readAt) receipt = 'read'
            else if (m.deliveredAt) receipt = 'delivered'
            else receipt = 'sent'
          }
          const mid = m._id ? String(m._id) : `hist-d-${contact.contactPhone}-${i}`
          return {
            id: mid,
            dbId: m._id ? String(m._id) : null,
            sender: fromMe ? 'Me' : m.senderPhone,
            senderLabel: fromMe ? 'You' : contact.contactName,
            text: m.message,
            highlight: false,
            receipt,
          }
        })
      )
      window.setTimeout(() => {
        socket.emit('read_receipt', {
          peerPhone: normPhone(contact.contactPhone),
        })
      }, 380)
      refreshPeerPresence(contact.contactPhone)
    } catch (err) {
      setChatLog([])
    }
  }

  const selectGroup = async (group) => {
    setPeerTyping(false)
    setPeerPresence({ online: false, lastSeenAt: null })
    setShowGroupInfo(false)
    setGroupInfo(null)
    setActiveChat(group)
    try {
      const res = await axiosWrapper.get(`/groups/history/${group._id}`, {
        headers: authHeader,
      })
      setChatLog(
        (res.data.messages || []).map((m, i) => ({
          id: m._id ? String(m._id) : `hist-g-${group._id}-${i}`,
          sender:
            normPhone(m.senderPhone) === normPhone(userData.phone)
              ? 'Me'
              : m.senderName || 'Member',
          senderLabel:
            normPhone(m.senderPhone) === normPhone(userData.phone)
              ? 'You'
              : m.senderName || 'Member',
          text: m.message || '',
          messageType: m.messageType || 'text',
          attachmentUrl: m.attachmentUrl || '',
          linkUrl: m.linkUrl || '',
          fileName: m.fileName || '',
          highlight: false,
        }))
      )
    } catch (err) {
      setChatLog([])
    }
  }

  const loadGroupInfo = useCallback(async () => {
    if (!isAdmin || !activeChat?._id) return
    setGroupInfoLoading(true)
    try {
      const res = await axiosWrapper.get(
        `/groups/admin/${activeChat._id}/members`,
        { headers: authHeader }
      )
      if (res.data?.success) setGroupInfo(res.data)
      else setGroupInfo(null)
    } catch {
      setGroupInfo(null)
      toast.error('Could not load group members')
    } finally {
      setGroupInfoLoading(false)
    }
  }, [isAdmin, activeChat?._id, authHeader])

  const adminClearGroupChat = async () => {
    if (!isAdmin || !activeChat?._id) return
    if (!window.confirm('Clear all messages in this group for everyone?')) return
    try {
      const res = await axiosWrapper.delete(
        `/groups/admin/${activeChat._id}/messages`,
        { headers: authHeader }
      )
      if (res.data?.success) {
        toast.success('Messages cleared')
        setChatLog([])
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to clear')
    }
  }

  const adminDeleteGroup = async () => {
    if (!isAdmin || !activeChat?._id) return
    if (
      !window.confirm(
        'Delete this group permanently? All members will lose access.'
      )
    )
      return
    try {
      const res = await axiosWrapper.delete(`/groups/admin/${activeChat._id}`, {
        headers: authHeader,
      })
      if (res.data?.success) {
        toast.success('Group deleted')
        setGroups((prev) => prev.filter((g) => String(g._id) !== String(activeChat._id)))
        setShowGroupInfo(false)
        setGroupInfo(null)
        setActiveChat(null)
        setChatLog([])
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete group')
    }
  }

  const uploadGroupFileAndSend = async (file) => {
    if (!file || view !== 'groups' || !activeChat?._id) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await axiosWrapper.post(`/groups/${activeChat._id}/upload`, fd, {
        headers: { ...authHeader },
      })
      if (!res.data?.success) throw new Error('Upload failed')
      const { attachmentUrl, fileName, mimeType } = res.data
      const isImage = guessImageFromMimeOrName(mimeType, fileName)
      const caption = (message || '').trim()
      socket.emit('group_message', {
        groupId: activeChat._id,
        senderPhone: normPhone(userData.phone),
        senderName: myDisplayName,
        message: caption,
        messageType: isImage ? 'image' : 'file',
        attachmentUrl,
        fileName: fileName || file.name,
        linkUrl: '',
      })
      setMessage('')
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Upload failed')
    }
  }

  useEffect(() => {
    if (showGroupInfo && isAdmin && activeChat?._id) {
      loadGroupInfo()
    }
  }, [showGroupInfo, isAdmin, activeChat?._id, loadGroupInfo])

  const toggleMemberSelection = (phone) => {
    setSelectedMembers((prev) =>
      prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]
    )
  }

  const createGroup = async () => {
    if (!newGroupName || selectedMembers.length === 0)
      return alert('Enter name and select members')
    try {
      const res = await axiosWrapper.post(
        `/groups/create`,
        {
          name: newGroupName,
          members: selectedMembers,
          creatorPhone: normPhone(userData.phone),
        },
        { headers: authHeader }
      )

      if (res.data.success) {
        const raw = res.data.group || {}
        const newGroup = {
          ...raw,
          members: [],
          memberCount:
            raw.memberCount ?? raw.members?.length ?? selectedMembers.length + 1,
        }
        setGroups([...groups, newGroup])

        // Notify members in real-time
        socket.emit('new_group_created', {
          groupId: newGroup._id,
          groupName: newGroup.name,
          members: selectedMembers,
        })

        setShowGroupModal(false)
        setSelectedMembers([])
        setNewGroupName('')
        socket.emit('join_group', newGroup._id)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const sendChat = () => {
    const text = typeof message === 'string' ? message.trim() : ''
    if (!text || !activeChat) return

    if (view === 'groups') {
      const linkUrl = detectSingleLink(text)
      const mt = linkUrl ? 'link' : 'text'
      socket.emit('group_message', {
        groupId: activeChat._id,
        senderPhone: normPhone(userData.phone),
        senderName: myDisplayName,
        message: text,
        messageType: mt,
        attachmentUrl: '',
        linkUrl: linkUrl || '',
        fileName: '',
      })
      setMessage('')
      return
    }

    const clientMsgId = genChatMsgId()
    socket.emit('typing_stop', { toPhone: normPhone(activeChat.contactPhone) })
    socket.emit('private_message', {
      sender: normPhone(userData.phone),
      receiver: normPhone(activeChat.contactPhone),
      message: text,
      clientMsgId,
    })
    const listPreview = text.length > 72 ? `${text.slice(0, 72)}…` : text
    const cp = normPhone(activeChat.contactPhone)
    const nowIso = new Date().toISOString()
    setContacts((prev) => {
      const idx = prev.findIndex((c) => normPhone(c.contactPhone) === cp)
      if (idx < 0) return prev
      const cur = prev[idx]
      const updated = {
        ...cur,
        lastMessagePreview: listPreview,
        lastMessageAt: nowIso,
        highlightFromOther: false,
        lastMessageFromSelf: true,
      }
      return [updated, ...prev.filter((_, i) => i !== idx)]
    })
    setChatLog((prev) => [
      ...prev,
      {
        id: clientMsgId,
        clientMsgId,
        dbId: null,
        sender: 'Me',
        senderLabel: 'You',
        text,
        highlight: false,
        receipt: 'sending',
      },
    ])
    setMessage('')
  }

  const initiateCall = () => {
    if (view === 'groups') {
      // Backend only supports direct phone-to-phone call signaling right now.
      return alert('Group calling is not implemented yet.')
    }

    setCallStatus('calling')
    const targetPhone = view === 'groups' ? activeChat._id : activeChat.contactPhone
    setCallTargetPhone(targetPhone)
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      localStreamRef.current = stream
      refreshAudioDevices()
      const peer = new Peer({ initiator: true, trickle: false, stream })
      peer.on('signal', (data) =>
        socket.emit('call_user', {
          userToCall: targetPhone,
          signal: data,
          from: userData.phone,
          name: myDisplayName,
        })
      )
      peer.on('stream', (rms) => {
        if (!remoteAudio.current) return
        remoteAudio.current.srcObject = rms
        // Some browsers need an explicit play() after srcObject is set.
        remoteAudio.current.play().catch(() => {})
      })
      connectionRef.current = peer
    })
  }

  const answerCall = () => {
    setReceivingCall(false)
    setCallStatus('connected')
    setCallTargetPhone(callerInfo.phone)
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      localStreamRef.current = stream
      refreshAudioDevices()
      const peer = new Peer({ initiator: false, trickle: false, stream })
      peer.on('signal', (data) =>
        socket.emit('answer_call', { signal: data, to: callerInfo.phone })
      )
      peer.on('stream', (rms) => {
        if (!remoteAudio.current) return
        remoteAudio.current.srcObject = rms
        remoteAudio.current.play().catch(() => {})
      })
      peer.signal(callerInfo.signal)
      connectionRef.current = peer
    })
  }

  return (
    <div style={styles.appContainer}>
      <audio ref={remoteAudio} autoPlay playsInline />

      {/* Sidebar Section */}
      <div style={styles.sidebar}>
        <div style={styles.tabSwitcher}>
          <button
            onClick={() => {
              setView('direct')
              setActiveChat(null)
              setChatLog([])
              setPeerTyping(false)
              setPeerPresence({ online: false, lastSeenAt: null })
            }}
            style={view === 'direct' ? styles.activeTab : styles.tab}
          >
            Direct
          </button>
          <button
            onClick={() => {
              setView('groups')
              setActiveChat(null)
              setChatLog([])
              setPeerTyping(false)
              setPeerPresence({ online: false, lastSeenAt: null })
            }}
            style={view === 'groups' ? styles.activeTab : styles.tab}
          >
            Groups
          </button>
        </div>

        <div style={styles.searchContainer}>
          <input
            style={styles.miniInput}
            placeholder="Verify Phone..."
            value={recipientInput}
            onChange={(e) => setRecipientInput(e.target.value)}
          />
          <button onClick={verifyAndAddContact} style={styles.addBtn}>
            Save
          </button>
        </div>

        <div style={styles.contactList}>
          {view === 'direct' ? (
            contacts.map((c) => {
              const rowActive =
                normPhone(activeChat?.contactPhone) === normPhone(c.contactPhone)
              const unreadCue =
                !!c.highlightFromOther && !rowActive && !!c.lastMessagePreview
              return (
                <div
                  key={normPhone(c.contactPhone) || c.contactName}
                  onClick={() => selectContact(c)}
                  style={{
                    ...styles.contactItem,
                    background: rowActive ? '#dbeafe' : unreadCue ? '#ecfdf5' : '#fff',
                    borderLeft: unreadCue
                      ? '4px solid #22c55e'
                      : '4px solid transparent',
                  }}
                  title={
                    c.lastMessagePreview
                      ? c.lastMessagePreview
                      : c.contactName
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <strong
                      style={{
                        color: unreadCue ? '#065f46' : '#0f172a',
                        fontSize: unreadCue ? '15px' : '14px',
                      }}
                    >
                      {c.contactName}
                    </strong>
                    {c.lastMessageAt ? (
                      <span
                        style={{
                          fontSize: '10px',
                          color: unreadCue ? '#047857' : '#94a3b8',
                          flexShrink: 0,
                          fontWeight: unreadCue ? 600 : 400,
                        }}
                      >
                        {formatSidebarTime(c.lastMessageAt)}
                      </span>
                    ) : null}
                  </div>
                  {isAdmin && (
                    <div style={{ fontSize: '10px', color: '#64748b' }}>
                      {c.contactPhone}
                    </div>
                  )}
                  {c.lastMessagePreview ? (
                    <div
                      style={{
                        fontSize: '12px',
                        marginTop: '6px',
                        lineHeight: 1.35,
                        color: unreadCue ? '#047857' : '#64748b',
                        fontWeight: c.highlightFromOther ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {c.lastMessageFromSelf
                        ? `You: ${c.lastMessagePreview}`
                        : c.lastMessagePreview}
                    </div>
                  ) : null}
                </div>
              )
            })
          ) : (
            <>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowGroupModal(true)}
                    style={styles.createGroupBtn}
                  >
                    + Create Group
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await axiosWrapper.post(
                          '/groups/admin/sync-semester-broadcasts',
                          {},
                          { headers: authHeader }
                        )
                        if (r.data?.success) {
                          toast.success(r.data.message || 'Semester groups synced')
                          fetchGroupsOnly()
                        }
                      } catch (e) {
                        toast.error(
                          e?.response?.data?.message || 'Could not sync semester groups'
                        )
                      }
                    }}
                    style={{
                      ...styles.createGroupBtn,
                      background: '#0f766e',
                      marginTop: 0,
                    }}
                  >
                    Sync semester broadcast groups
                  </button>
                </>
              )}
              {groups.map((g) => (
                <div
                  key={g._id}
                  onClick={() => selectGroup(g)}
                  style={{
                    ...styles.contactItem,
                    background:
                      activeChat?._id === g._id ? '#e2e8f0' : 'transparent',
                  }}
                >
                  <strong>{g.name}</strong>
                  <small style={{ display: 'block' }}>
                    {g.memberCount ?? g.members?.length ?? 0} members
                  </small>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div style={styles.mainChat}>
        {activeChat ? (
          <>
            <div style={styles.chatHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: 0, fontSize: '17px' }}>
                  {view === 'groups' ? activeChat.name : activeChat.contactName}
                </h4>
                {view === 'direct' ? (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: '12px',
                      color: peerTyping
                        ? '#047857'
                        : peerPresence.online
                          ? '#16a34a'
                          : '#64748b',
                      fontWeight: peerTyping ? 600 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {peerTyping
                      ? 'typing…'
                      : peerPresence.online
                        ? 'online'
                        : peerPresence.lastSeenAt
                          ? formatLastSeenLabel(peerPresence.lastSeenAt)
                          : 'offline'}
                  </div>
                ) : (
                  <div style={{ marginTop: 4, fontSize: '11px', color: '#64748b' }}>
                    {activeChat.memberCount != null
                      ? `${activeChat.memberCount} members · links & files supported`
                      : 'Group chat · links & files supported'}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  flexShrink: 0,
                }}
              >
                {isAdmin && view === 'groups' && (
                  <button
                    type="button"
                    onClick={() => setShowGroupInfo(true)}
                    style={{
                      ...styles.callBtn,
                      background: '#475569',
                      padding: '6px 12px',
                    }}
                    title="Group info (admin)"
                  >
                    ℹ️ Info
                  </button>
                )}
                <button
                  onClick={() => {
                    if (view === 'groups') {
                      return alert('Group calling is not implemented yet.')
                    }
                    initiateCall()
                  }}
                  style={{
                    ...styles.callBtn,
                    opacity: view === 'groups' ? 0.5 : 1,
                    cursor: view === 'groups' ? 'not-allowed' : 'pointer',
                  }}
                  disabled={view === 'groups'}
                  title={view === 'groups' ? 'Group calls not implemented yet' : 'Start voice call'}
                >
                  📞 Call
                </button>
              </div>
            </div>
            <div style={styles.messageArea}>
              {chatLog.map((chat, idx) => {
                const isOwn = chat.sender === 'Me'
                const caption = chat.senderLabel || chat.sender
                return (
                  <div
                    key={chat.id ?? `row-${idx}`}
                    style={{
                      display: 'flex',
                      justifyContent: isOwn ? 'flex-end' : 'flex-start',
                      margin: '3px 0 5px',
                      padding: '0 6px',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: 'min(82%, 360px)',
                        textAlign: isOwn ? 'right' : 'left',
                      }}
                    >
                      {!isOwn && (
                        <div
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#667781',
                            margin: '0 2px 3px 4px',
                            letterSpacing: '0.01em',
                          }}
                        >
                          {caption}
                        </div>
                      )}
                      <span
                        className={
                          !isOwn && chat.highlight
                            ? 'chat-msg-bubble--highlight'
                            : undefined
                        }
                        style={{
                          display: 'inline-block',
                          padding: '7px 11px 8px',
                          fontSize: '14.5px',
                          lineHeight: 1.45,
                          wordBreak: 'break-word',
                          background: isOwn ? '#d9fdd3' : '#fff',
                          color: '#111b21',
                          borderRadius: isOwn
                            ? '10px 10px 4px 10px'
                            : '10px 10px 10px 4px',
                          boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)',
                          border: isOwn ? 'none' : '1px solid rgba(11,20,26,0.06)',
                        }}
                      >
                        {view === 'groups' ? (
                          <GroupMessageBody chat={chat} />
                        ) : (
                          chat.text
                        )}
                      </span>
                      {isOwn && view === 'direct' && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '5px',
                            marginTop: '3px',
                            paddingRight: '2px',
                          }}
                        >
                          <MessageReceiptTick receipt={chat.receipt || 'sent'} />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              <div ref={chatEndRef} />
            </div>
            <div style={styles.inputArea}>
              {view === 'groups' && (
                <>
                  <input
                    type="file"
                    ref={groupFileRef}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.txt"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) uploadGroupFileAndSend(f)
                      e.target.value = ''
                    }}
                  />
                  <button
                    type="button"
                    title="Attach image or document"
                    onClick={() => groupFileRef.current?.click()}
                    style={{
                      border: '1px solid #cbd5e1',
                      background: '#f8fafc',
                      borderRadius: '50%',
                      width: 42,
                      height: 42,
                      cursor: 'pointer',
                      fontSize: 18,
                    }}
                  >
                    📎
                  </button>
                </>
              )}
              <input
                style={styles.mainInput}
                value={message}
                onChange={(e) => {
                  const v = e.target.value
                  setMessage(v)
                  if (view !== 'direct' || !activeChat?.contactPhone) return
                  if (v.trim() === '') return
                  socket.emit('typing', {
                    toPhone: normPhone(activeChat.contactPhone),
                  })
                  if (typingStopTimerRef.current) {
                    window.clearTimeout(typingStopTimerRef.current)
                  }
                  typingStopTimerRef.current = window.setTimeout(() => {
                    socket.emit('typing_stop', {
                      toPhone: normPhone(activeChat.contactPhone),
                    })
                  }, 2200)
                }}
                onBlur={() => {
                  if (view === 'direct' && activeChat?.contactPhone) {
                    socket.emit('typing_stop', {
                      toPhone: normPhone(activeChat.contactPhone),
                    })
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && sendChat()}
                placeholder={
                  peerTyping && view === 'direct'
                    ? 'Reply while they type…'
                    : view === 'groups'
                      ? 'Message or paste a link (https://…)'
                      : 'Type message...'
                }
              />
              <button onClick={sendChat} style={styles.sendBtn}>
                ➤
              </button>
            </div>
          </>
        ) : (
          <div style={styles.emptyState}>
            Select a chat from the sidebar to start
          </div>
        )}
      </div>

      {/* Admin: group info (members + phones) + clear / delete */}
      {showGroupInfo && isAdmin && activeChat && (
        <div style={styles.modalOverlay}>
          <div
            style={{
              ...styles.modal,
              width: 'min(96vw, 420px)',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h4 style={{ marginBottom: 8 }}>Group info</h4>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
              <strong>{groupInfo?.group?.name || activeChat.name}</strong>
              {groupInfo?.group?.broadcastSemester != null
                ? ` · Semester ${groupInfo.group.broadcastSemester}`
                : ''}
            </p>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                marginBottom: 12,
                maxHeight: '42vh',
              }}
            >
              {groupInfoLoading ? (
                <p style={{ padding: 12, fontSize: 13 }}>Loading members…</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                      <th style={{ padding: 8 }}>Name</th>
                      <th style={{ padding: 8 }}>Phone</th>
                      <th style={{ padding: 8 }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(groupInfo?.members || []).map((m) => (
                      <tr key={m.phone} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: 8, fontWeight: 600 }}>{m.displayName}</td>
                        <td style={{ padding: 8, fontFamily: 'monospace' }}>{m.phone}</td>
                        <td style={{ padding: 8, textTransform: 'capitalize' }}>{m.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={adminClearGroupChat}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 8,
                  border: '1px solid #f59e0b',
                  background: '#fffbeb',
                  color: '#92400e',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Clear group chat
              </button>
              <button
                type="button"
                onClick={adminDeleteGroup}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Delete group
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGroupInfo(false)
                  setGroupInfo(null)
                }}
                style={styles.cancelLink}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h4 style={{ marginBottom: '10px' }}>New Group</h4>
            <input
              placeholder="Group Name"
              onChange={(e) => setNewGroupName(e.target.value)}
              style={{
                ...styles.miniInput,
                width: '100%',
                marginBottom: '10px',
              }}
            />
            <div style={styles.selectionList}>
              {contacts.map((c, i) => (
                <label key={i} style={styles.selectionItem}>
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(c.contactPhone)}
                    onChange={() => toggleMemberSelection(c.contactPhone)}
                  />
                  <span style={{ marginLeft: '8px' }}>{c.contactName}</span>
                </label>
              ))}
            </div>
            <button
              onClick={createGroup}
              style={{ ...styles.callBtn, width: '100%', marginTop: '10px' }}
            >
              Create Group
            </button>
            <button
              onClick={() => setShowGroupModal(false)}
              style={styles.cancelLink}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Calling Overlays */}
      {receivingCall && (
        <div style={styles.incomingBox}>
          <p>{callerInfo.name} is calling...</p>
          <button onClick={answerCall} style={styles.acceptBtn}>
            Accept
          </button>
          <button
            onClick={() => {
              setReceivingCall(false)
              setCallStatus('idle')
              setCallerInfo({ phone: '', name: '', signal: null })
              setCallTargetPhone('')
              setDeviceError('')
              // Notify caller so both ends stop trying.
              if (callerInfo?.phone) {
                socket.emit('end_call', { to: callerInfo.phone, from: userData.phone })
              }
            }}
            style={styles.declineBtn}
          >
            Ignore
          </button>
        </div>
      )}

      {callStatus !== 'idle' && (
        <div style={styles.callOverlay}>
          <h2>{callStatus.toUpperCase()}</h2>

          {(audioDevices.inputs.length > 0 || audioDevices.outputs.length > 0) && (
            <div
              style={{
                display: 'grid',
                gap: 10,
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              {audioDevices.inputs.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>Mic</span>
                  <select
                    value={micDeviceId}
                    onChange={(e) => setMicDeviceId(e.target.value)}
                    style={{ flex: 1, padding: 6, borderRadius: 8 }}
                  >
                    {audioDevices.inputs.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={switchMicrophone}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      background: '#1e3a8a',
                      color: '#fff',
                    }}
                  >
                    Switch
                  </button>
                </div>
              ) : null}

              {audioDevices.outputs.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>Speaker</span>
                  <select
                    value={speakerDeviceId}
                    onChange={(e) => setSpeakerDeviceId(e.target.value)}
                    style={{ flex: 1, padding: 6, borderRadius: 8 }}
                  >
                    {audioDevices.outputs.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={switchSpeaker}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      background: '#1e3a8a',
                      color: '#fff',
                    }}
                  >
                    Switch
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {deviceError ? (
            <div style={{ color: '#b91c1c', marginTop: 10, fontSize: 12 }}>{deviceError}</div>
          ) : null}

          <button
            onClick={endCall}
            style={styles.hangupBtn}
          >
            End Call
          </button>
        </div>
      )}
    </div>
  )
}

// ... Include Styles Object (Same as previous) ...
const styles = {
  appContainer: {
    display: 'flex',
    width: '100%',
    height: '85vh',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #ddd',
  },
  sidebar: {
    width: '300px',
    borderRight: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
  },
  tabSwitcher: { display: 'flex', background: '#f8fafc' },
  tab: {
    flex: 1,
    padding: '15px',
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    background: 'transparent',
    cursor: 'pointer',
  },
  activeTab: {
    flex: 1,
    padding: '15px',
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
    borderBottomColor: '#1e3a8a',
    background: '#fff',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  searchContainer: {
    padding: '10px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    gap: '5px',
  },
  miniInput: {
    flex: 1,
    padding: '8px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    outline: 'none',
  },
  addBtn: {
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '0 10px',
    cursor: 'pointer',
  },
  contactList: { flex: 1, overflowY: 'auto' },
  contactItem: {
    padding: '12px 14px',
    borderBottom: '1px solid #e2e8f0',
    cursor: 'pointer',
    borderLeft: '4px solid transparent',
    background: '#fff',
    boxSizing: 'border-box',
  },
  createGroupBtn: {
    width: '90%',
    margin: '10px auto',
    padding: '10px',
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'block',
  },
  mainChat: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#e5ddd5',
  },
  chatHeader: {
    padding: '14px 20px',
    background: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    borderBottom: '1px solid #eee',
  },
  messageArea: {
    flex: 1,
    padding: '10px 8px 14px',
    overflowY: 'auto',
    background: '#e5ddd5',
  },
  inputArea: {
    padding: '15px',
    background: '#fff',
    display: 'flex',
    gap: '10px',
  },
  mainInput: {
    flex: 1,
    padding: '10px 15px',
    borderRadius: '20px',
    border: '1px solid #ddd',
    outline: 'none',
  },
  sendBtn: {
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    cursor: 'pointer',
  },
  callBtn: {
    background: '#22c55e',
    color: '#fff',
    border: 'none',
    padding: '5px 15px',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  emptyState: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    padding: '20px',
    borderRadius: '12px',
    width: '300px',
  },
  selectionList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #eee',
    borderRadius: '5px',
    padding: '5px',
  },
  selectionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    cursor: 'pointer',
    borderBottom: '1px solid #f9f9f9',
  },
  cancelLink: {
    width: '100%',
    marginTop: '5px',
    border: 'none',
    background: 'none',
    color: '#ef4444',
    cursor: 'pointer',
  },
  incomingBox: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#fff',
    padding: '20px',
    borderRadius: '15px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
    zIndex: 101,
  },
  acceptBtn: {
    background: '#22c55e',
    color: '#fff',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '8px',
    marginRight: '10px',
  },
  declineBtn: {
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '8px',
  },
  callOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(30, 58, 138, 0.95)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  hangupBtn: {
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '20px',
    marginTop: '20px',
    cursor: 'pointer',
  },
}

export default CommunicationHub
